/**
 * Document upload
 * Files are a data source too: contracts, board packs and spreadsheets are
 * read into the same layer as the systems. Carried over from the live product,
 * which accepts PDF, Word, Excel, CSV, TXT and JSON up to 50MB.
 */

import React, { useRef, useState } from 'react';
import { Icon, ICONS } from '../shell/icons';
import { Tag, Table } from '../ui';
import { UPLOADED_DOCS, UPLOAD_ACCEPT } from '../../demo/live';

const TYPE_COLORS: Record<string, string> = {
  PDF: '#CE4630',
  Word: '#2354F0',
  Excel: '#0A9467',
  CSV: '#0E9B8E',
  TXT: '#69747A',
  JSON: '#6D5AE6',
};

function typeOf(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return 'PDF';
  if (ext === 'doc' || ext === 'docx') return 'Word';
  if (ext === 'xls' || ext === 'xlsx') return 'Excel';
  if (ext === 'csv') return 'CSV';
  if (ext === 'json') return 'JSON';
  return 'TXT';
}

export const DocumentUpload: React.FC = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [docs, setDocs] = useState(UPLOADED_DOCS);

  const accept = (files: FileList | null) => {
    if (!files?.length) return;
    const added = Array.from(files).map((f) => ({
      name: f.name,
      type: typeOf(f.name),
      size: f.size > 1048576 ? `${(f.size / 1048576).toFixed(1)} MB` : `${Math.round(f.size / 1024)} KB`,
      added: 'Just now',
      status: 'Indexing',
      tone: 'b',
      pages: 0,
    }));
    setDocs([...added, ...docs]);
  };

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-hd">
        <h4>Documents</h4>
        <span className="s">read into the same layer as the systems</span>
        <div className="r">
          <Tag tone="b">{docs.length} in the knowledge base</Tag>
        </div>
      </div>

      <div className="card-b">
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.json"
          style={{ display: 'none' }}
          onChange={(e) => accept(e.target.files)}
        />

        <button
          className="dropzone"
          data-over={over || undefined}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            accept(e.dataTransfer.files);
          }}
        >
          <span className="kic" style={{ background: 'var(--blue-s)', color: 'var(--blue)' }}>
            <Icon d={ICONS.upload} />
          </span>
          <div style={{ fontSize: 13, fontWeight: 700, marginTop: 8 }}>
            Click to upload, or drag files here
          </div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>
            {UPLOAD_ACCEPT}
          </div>
        </button>

        <div style={{ marginTop: 12 }}>
          <Table
            maxHeight={260}
            cols={[
              { t: 'Document' },
              { t: 'Type' },
              { t: 'Size', num: true },
              { t: 'Pages', num: true },
              { t: 'Added' },
              { t: 'Status' },
            ]}
            rows={docs.map((d) => [
              <span className="mono" style={{ fontSize: 12, fontWeight: 700 }}>
                {d.name}
              </span>,
              <span
                className="tg"
                style={{
                  background: `color-mix(in srgb, ${TYPE_COLORS[d.type] || '#69747A'} 12%, #fff)`,
                  color: TYPE_COLORS[d.type] || '#69747A',
                }}
              >
                {d.type}
              </span>,
              d.size,
              d.pages || '—',
              <span className="dim">{d.added}</span>,
              <Tag tone={d.tone as any}>{d.status}</Tag>,
            ])}
          />
        </div>
      </div>
    </div>
  );
};
